import React from "react";
import useModalLayout, { type ModalSize } from "../../hooks/useModalLayout";

export interface ModalContainerProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  size?: ModalSize;
  showCloseButton?: boolean;
  closeOnBackdropClick?: boolean;
  autoHeight?: boolean;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  customHeaderContent?: React.ReactNode;
  footerClassName?: string;
  titleClassName?: string;
  fullscreenOverlay?: boolean;
  hideDefaultTitle?: boolean;
}

export const ModalContainer: React.FC<ModalContainerProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  size = "lg",
  showCloseButton = true,
  closeOnBackdropClick = true,
  autoHeight = false,
  children,
  footer,
  className = "",
  customHeaderContent,
  footerClassName,
  titleClassName,
  fullscreenOverlay,
  hideDefaultTitle = false,
}) => {
  const { classes, isDesktop } = useModalLayout({
    size,
    autoHeight,
    fullscreenOverlay,
  });

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (closeOnBackdropClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Modal Overlay with integrated backdrop */}
      <div
        className={classes.container}
        onClick={handleBackdropClick}
        data-testid="modal-overlay"
        role="dialog"
        aria-modal="true"
      >
        {/* Modal Content */}
        <div
          className={`${classes.content} ${className}`}
          onClick={(e) => e.stopPropagation()}
          data-testid="modal-container"
        >
          {/* Header */}
          <div
            className="tw-p-6 tw-flex tw-item-start tw-justify-between tw-border-b tw-border-gray-200 tw-flex-shrink-0"
            data-testid="modal-header"
          >
            {hideDefaultTitle ? (
              // When hideDefaultTitle is true, render ONLY customHeaderContent
              // customHeaderContent handles its own layout including close button
              customHeaderContent
            ) : (
              <>
                {/* Title Row - Default behavior */}
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                  <div className="tw-flex tw-flex-col">
                    <h2
                      className={`tw-text-xl tw-font-bold tw-text-gray-900 ${
                        titleClassName || ""
                      }`}
                      data-testid="modal-title"
                    >
                      {title}
                    </h2>
                    {subtitle && (
                      <p
                        className="tw-text-sm tw-text-gray-500 tw-mt-1"
                        data-testid="modal-subtitle"
                      >
                        {subtitle}
                      </p>
                    )}
                  </div>

                  {showCloseButton && (
                    <button
                      onClick={onClose}
                      className="-tw-mr-2 tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors tw-rounded-lg hover:tw-bg-gray-100"
                      aria-label="Close modal"
                      data-testid="modal-close-button"
                    >
                      <span className="material-symbols-outlined tw-text-2xl">
                        close
                      </span>
                    </button>
                  )}
                </div>

                {/* Custom Header Content - Below title */}
                {customHeaderContent && (
                  <div className="tw-space-y-4">{customHeaderContent}</div>
                )}
              </>
            )}
          </div>

          {/* Content - Conditionally Scrollable Area */}
          <div
            className={
              autoHeight
                ? "tw-overflow-y-auto tw-max-h-[calc(90vh-8rem)]"
                : "tw-flex-1 tw-overflow-y-auto tw-min-h-0"
            }
            data-testid="modal-content"
          >
            {children}
          </div>

          {/* Footer */}
          {footer && (
            <div
              className={`tw-p-6 tw-bg-white tw-border-t tw-border-gray-200 tw-rounded-b-xl tw-flex-shrink-0 ${
                footerClassName || ""
              }`}
              data-testid="modal-footer"
            >
              {footer}
            </div>
          )}
        </div>
      </div>

      {/* Mobile full-screen overlay for better mobile UX */}
      {!isDesktop && (
        <div
          className="tw-fixed tw-inset-0 tw-bg-white tw-z-40"
          style={{ display: isOpen ? "block" : "none" }}
        />
      )}
    </>
  );
};

export default ModalContainer;
